/**
 * Supervision FHIR Constants
 *
 * Centralizes extension URLs, coding systems, and helper types for the
 * supervision section's FHIR model. All supervision-specific extensions
 * and codings are defined here to avoid scattered magic strings.
 *
 * FHIR model summary:
 *   - Cosign queue / document review: QuestionnaireResponse (existing)
 *   - Missing documents: Task (owner=clinician, for=patient, code=doc-type)
 *   - Licenses: Practitioner.qualification (native FHIR)
 *   - Overtime & Admin: Observation (subject=Practitioner)
 *   - Note quality: Observation (subject=Practitioner, focus=QR)
 *   - Review timeline: Provenance (target=QR, agent=actor)
 */

// Re-export existing QR-level extension URLs used by supervision
export {
  PROVIDER_SIGNATURE_EXT_URL,
  SUPERVISOR_SIGNATURE_EXT_URL,
  REVIEW_COMMENTS_EXT_URL,
  REQUESTED_EDITS_EXT_URL,
  REQUESTED_EDITS_TAG_SYSTEM,
  REQUESTED_EDITS_TAG_CODE,
  APPOINTMENT_TAG_SYSTEM,
  SUPERVISING_PRACTITIONER_EXT_URL,
} from '../appointments/fhirNoteMapping';

// ─────────────────────────────────────────────────────────────────────
// Missing Documents (Task) coding systems
// ─────────────────────────────────────────────────────────────────────

export const SUPERVISION_TASK_SYSTEM = 'https://unify.health/fhir/supervision/task-type';

export const SUPERVISION_TASK_CODES = {
  'missing-notes': { system: SUPERVISION_TASK_SYSTEM, code: 'missing-notes', display: 'Missing Progress Notes' },
  'treatment-plans': { system: SUPERVISION_TASK_SYSTEM, code: 'treatment-plans', display: 'Treatment Plans' },
  forms: { system: SUPERVISION_TASK_SYSTEM, code: 'forms', display: 'Forms' },
} as const;

export const SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM = 'https://unify.health/fhir/supervision/form-subtype';

export const SUPERVISION_TASK_FORM_SUBTYPES = {
  roi: { system: SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM, code: 'roi', display: 'ROI' },
  'custody-agreement': { system: SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM, code: 'custody-agreement', display: 'Custody Agreement' },
  intake: { system: SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM, code: 'intake', display: 'Intake Form' },
  'discharge-summary': { system: SUPERVISION_TASK_FORM_SUBTYPE_SYSTEM, code: 'discharge-summary', display: 'Discharge Summary' },
} as const;

export const SUPERVISION_TASK_CATEGORY_SYSTEM = 'https://unify.health/fhir/supervision/task-category';
export const SUPERVISION_TASK_CATEGORY_CODE = 'missing-document';

// ─────────────────────────────────────────────────────────────────────
// Compliance: Overtime & Admin (Observation) coding systems
// ─────────────────────────────────────────────────────────────────────

export const COMPLIANCE_OBSERVATION_SYSTEM = 'https://unify.health/fhir/supervision/compliance';

export const COMPLIANCE_OBS_CODES = {
  weeklyHours: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'weekly-hour-total', display: 'Weekly Hour Total' },
  adminOverage: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'admin-hour-overage', display: 'Admin Hour Overage' },
  dailyHours: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'daily-hour-total', display: 'Daily Hour Total' },
  monthsFullTime: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'months-over-130', display: 'Months Over 130 Hours' },
  monthlyHours: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'monthly-hour-total', display: 'Monthly Hour Total' },
} as const;

export const COMPLIANCE_OBS_CATEGORY = {
  system: 'https://unify.health/fhir/supervision/observation-category',
  code: 'compliance',
  display: 'Compliance',
} as const;

export const COMPLIANCE_COMPONENT_CODES = {
  overtimeHours: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'overtime-hours', display: 'Overtime Hours' },
  adminHours: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'admin-hours', display: 'Admin Hours' },
  clinicalHours: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'clinical-hours', display: 'Clinical Hours' },
  adminRatio: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'admin-ratio', display: 'Admin Ratio' },
  totalHours: { system: COMPLIANCE_OBSERVATION_SYSTEM, code: 'total-hours', display: 'Total Hours' },
} as const;

// ─────────────────────────────────────────────────────────────────────
// Note Quality (Observation) coding systems
// ─────────────────────────────────────────────────────────────────────

export const NOTE_QUALITY_OBSERVATION_SYSTEM = 'https://unify.health/fhir/supervision/note-quality';

export const NOTE_QUALITY_OBS_CODES = {
  editDistance: { system: NOTE_QUALITY_OBSERVATION_SYSTEM, code: 'edit-distance', display: 'Edit Distance Warning' },
  adjustmentDisorder: { system: NOTE_QUALITY_OBSERVATION_SYSTEM, code: 'adjustment-disorder', display: 'Adjustment Disorder Warning' },
} as const;

export const NOTE_QUALITY_OBS_CATEGORY = {
  system: 'https://unify.health/fhir/supervision/observation-category',
  code: 'note-quality',
  display: 'Note Quality',
} as const;

export const NOTE_QUALITY_COMPONENT_CODES = {
  editDistanceValue: { system: NOTE_QUALITY_OBSERVATION_SYSTEM, code: 'edit-distance-value', display: 'Edit Distance Value' },
  warningType: { system: NOTE_QUALITY_OBSERVATION_SYSTEM, code: 'warning-type', display: 'Warning Type' },
} as const;

// ─────────────────────────────────────────────────────────────────────
// Review Timeline (Provenance) activity codes
// ─────────────────────────────────────────────────────────────────────

export const SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM = 'https://unify.health/fhir/supervision/provenance-activity';

export const PROVENANCE_ACTIVITY_CODES = {
  commentAdded: { system: SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM, code: 'comment-added', display: 'Comment Added' },
  commentResolved: { system: SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM, code: 'comment-resolved', display: 'Comment Resolved' },
  requestedEdits: { system: SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM, code: 'requested-edits', display: 'Requested Edits' },
  reviewed: { system: SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM, code: 'reviewed', display: 'Reviewed' },
  signed: { system: SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM, code: 'signed', display: 'Signed' },
  noteEdited: { system: SUPERVISION_PROVENANCE_ACTIVITY_SYSTEM, code: 'note-edited', display: 'Note Edited' },
} as const;

export const PROVENANCE_DETAIL_EXT_URL = 'https://unify.health/fhir/supervision/provenance-detail';

// ─────────────────────────────────────────────────────────────────────
// Supervision review status extension (on QuestionnaireResponse)
// ─────────────────────────────────────────────────────────────────────

export const SUPERVISION_REVIEW_STATUS_EXT_URL = 'https://unify.health/fhir/supervision/review-status';

// ─────────────────────────────────────────────────────────────────────
// Supervision Meeting Notes (non-appointment Clinical Supervision/Supervisor QRs)
// ─────────────────────────────────────────────────────────────────────

/**
 * Tag system used to mark a QuestionnaireResponse as a supervision meeting note.
 * These are standalone (not appointment-linked) notes for the supervision relationship.
 */
export const SUPERVISION_MEETING_TAG_SYSTEM = 'https://unify.health/fhir/supervision/meeting-note';

/** Tag system distinguishing supervisee vs supervisor note types within supervision meetings. */
export const SUPERVISION_NOTE_TYPE_TAG_SYSTEM = 'https://unify.health/fhir/supervision/note-type';

export const SUPERVISION_NOTE_TYPE_CODES = {
  /** Clinical Supervision Note — authored by supervisee/provider, requires supervisor cosign. */
  'clinical-supervision': 'clinical-supervision',
  /** Clinical Supervisor Note — authored by supervisor only, locked after supervisor signature. */
  'clinical-supervisor': 'clinical-supervisor',
} as const;

export type SupervisionNoteType = keyof typeof SUPERVISION_NOTE_TYPE_CODES;

/** Canonical identifier slugs for supervision meeting note templates. */
export const SUPERVISION_MEETING_TEMPLATE_SLUGS = {
  superviseeNote: 'meridian-counseling-clinical-supervision-note',
  supervisorNote: 'meridian-counseling-clinical-supervisor-note',
  groupSuperviseeNote: 'meridian-counseling-group-supervision-note',
  groupSupervisorNote: 'meridian-counseling-group-supervisor-note',
} as const;
